import React from 'react'

/**
 * Renders a CMS heading string with `*asterisk-wrapped*` runs set in italic and literal `\n`
 * as a hard line break.
 *
 * The Wilson design language emphasises one word or phrase per heading in Merriweather
 * italic — "Taking *Traralgon* Home.", "Our *Track Record*", "Your Next Move, *Right Here*",
 * "Fiercely *Traralgon*. Fiercely *Local*." Editors express that in plain CMS text fields
 * using asterisks, and this converts those runs to `<em>`.
 *
 * Some Figma headings also have an intentional hard break rather than relying on responsive
 * wrap — e.g. "*The People* Taking\nTraralgon Home" breaks after "Taking" at every breakpoint,
 * not wherever the container happens to wrap. Editors express that with a literal `\n`.
 *
 * Previously this exact function was copy-pasted into four block components
 * (FeaturedListings/TabbedListings, TrustIndicators, TestimonialCarousel, Awards/AwardsStrip),
 * with a fifth copy pending for TextAndImage. BRIEF §0 requires no duplicate implementations,
 * so it now lives here and all of them import it.
 *
 * Note: headings that come through Lexical rich text already carry real `<em>` / line-break
 * nodes and do NOT need this — it is only for plain-string heading fields.
 */
export function renderHeading(heading: string): React.ReactNode[] {
  const withBreaks = (text: string, keyPrefix: string): React.ReactNode[] =>
    text
      .split('\n')
      .flatMap((line, i, arr) =>
        i < arr.length - 1 ? [line, <br key={`${keyPrefix}-br-${i}`} />] : [line],
      )

  return heading.split(/\*([^*]+)\*/g).flatMap((part, i): React.ReactNode[] =>
    i % 2 === 1
      ? [
          <em key={i} className="italic">
            {withBreaks(part, `em-${i}`)}
          </em>,
        ]
      : withBreaks(part, `t-${i}`),
  )
}
